// @em/blocks — the organizer blocks' resolvers (ROADMAP B25 / B26).
//
//  • `resolveOrganizer` — the event's organizer credit (`PublicEvent.organizer`: the profile's PUBLIC facts, the same ones
//    `/o/<slug>` shows) → the organizer footer block's `OrganizerPreview`. Null without a profile (or for a draft — the
//    server already answers null then).
//  • `resolveOrganizerEvents` / `filterOrganizerEventsForWidget` — the organizer's other UPCOMING events (`page.data.
//    organizerEvents`, already gated and ordered by the server: soonest NEXT date first, a recurring event ONCE) → what
//    one organizer-events block shows: every one, or only this event's channel, up to its count.
// Pure, so the guards run them.
import type { PublicEventOrganizer, PublicOrganizerEvent } from '@em/api';

import type { OrganizerEventPreview, OrganizerPreview } from '../blockContext';
import type { WidgetConfig } from '../config';

/** The block's count: 1–24, default 6. */
export const ORGANIZER_EVENTS_DEFAULT_LIMIT = 6;
export const ORGANIZER_EVENTS_MAX_LIMIT = 24;

const text = (value: unknown): string | null => {
  const out = typeof value === 'string' ? value.trim() : '';

  return out || null;
};

export function resolveOrganizer(organizer: PublicEventOrganizer | null | undefined): OrganizerPreview | null {
  if (!organizer) {
    return null;
  }

  const address = [organizer.placeName, organizer.streetAddress, organizer.city, organizer.stateProvince, organizer.postalCode, organizer.country]
    .map(text)
    .filter(Boolean)
    .join(', ');

  return {
    name: text(organizer.name) ?? 'Organizer',
    slug: text(organizer.slug),
    summary: text(organizer.summary),
    logoUrl: text(organizer.logoUploadedFile?.thumbnailUrl) ?? text(organizer.logoUploadedFile?.fileUrl),
    website: text(organizer.website),
    email: text(organizer.email),
    phone: text(organizer.phone),
    socialLinks: (organizer.socialUrls ?? []).map(text).filter((url): url is string => !!url),
    addressLine: address || null,
  };
}

export function resolveOrganizerEvents(events: (PublicOrganizerEvent | null)[] | null | undefined): OrganizerEventPreview[] {
  return (events ?? []).filter((e): e is PublicOrganizerEvent => !!e && !!e.id);
}

/** The block's count from its config: a whole number in 1–24, else the default. */
export const organizerEventsLimitOf = (config: WidgetConfig | null | undefined): number => {
  const n = Math.floor(Number(config?.organizerEventsLimit));

  return Number.isFinite(n) && n >= 1 ? Math.min(n, ORGANIZER_EVENTS_MAX_LIMIT) : ORGANIZER_EVENTS_DEFAULT_LIMIT;
};

const sameChannel = (a: unknown, b: unknown): boolean => {
  const x = text(a)?.toLowerCase();

  return !!x && x === text(b)?.toLowerCase();
};

/**
 * What ONE organizer-events block shows: every upcoming event of the organizer, or (mode "channel") only those listed
 * under this event's channel — none when this event has no channel (there is nothing to be "related" by) — up to the
 * block's count, in the server's order.
 */
export function filterOrganizerEventsForWidget(
  config: WidgetConfig | null | undefined,
  events: OrganizerEventPreview[],
  channel: string | null | undefined,
): OrganizerEventPreview[] {
  const byChannel = config?.organizerEventsMode === 'channel';
  const shown = byChannel ? events.filter((e) => sameChannel(e.channel, channel)) : events;

  return shown.slice(0, organizerEventsLimitOf(config));
}
